import { useState, useEffect, useRef } from 'react';

interface BulkDeck {
  id: string;
  topic: string;
  status: 'queued' | 'generating' | 'done';
  progress: number;
  slideCount: number;
}

interface Props { onSignUp: () => void; }

const TONES = ['Professional', 'Casual', 'Academic', 'Persuasive', 'Inspiring'];
const SLIDE_COUNTS = [10, 11, 10, 12, 11, 10, 12, 11, 10, 12];

export function BulkGeneratePanel({ onSignUp }: Props) {
  const [bulkTopics, setBulkTopics] = useState('');
  const [tone, setTone]             = useState('Professional');
  const [decks, setDecks]           = useState<BulkDeck[]>([]);
  const [isRunning, setIsRunning]   = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const topics    = bulkTopics.split('\n').map(t => t.trim()).filter(Boolean).slice(0, 10);
  const doneCount = decks.filter(d => d.status === 'done').length;
  const allDone   = doneCount === decks.length && decks.length > 0 && !isRunning;

  const startBatch = () => {
    if (topics.length === 0) return;
    const initial: BulkDeck[] = topics.map((topic, i) => ({
      id: `bulk-${i}`, topic, status: 'queued', progress: 0, slideCount: SLIDE_COUNTS[i % SLIDE_COUNTS.length],
    }));
    setDecks(initial);
    setCurrentIdx(0);
    setIsRunning(true);
  };

  // Process decks one at a time
  useEffect(() => {
    if (!isRunning || currentIdx < 0 || currentIdx >= decks.length) return;
    setDecks(prev => prev.map((d, i) => i === currentIdx ? { ...d, status: 'generating', progress: 0 } : d));
    let prog = 0;
    intervalRef.current = setInterval(() => {
      prog += Math.random() * 14 + 6;
      if (prog >= 100) {
        prog = 100;
        clearInterval(intervalRef.current!);
        setDecks(prev => prev.map((d, i) => i === currentIdx ? { ...d, status: 'done', progress: 100 } : d));
        setCurrentIdx(idx => idx + 1);
      } else {
        setDecks(prev => prev.map((d, i) => i === currentIdx ? { ...d, progress: prog } : d));
      }
    }, 110);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [currentIdx, isRunning, decks.length]);

  useEffect(() => {
    if (isRunning && currentIdx >= decks.length && decks.length > 0) setIsRunning(false);
  }, [currentIdx, decks.length, isRunning]);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const handleReset = () => { setDecks([]); setBulkTopics(''); setCurrentIdx(-1); setIsRunning(false); };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-center gap-2.5 p-3 bg-amber-500/10 border border-amber-500/25 rounded-xl">
        <i className="ri-stack-line text-amber-400 text-base flex-shrink-0"></i>
        <p className="text-xs font-semibold text-amber-300">Batch mode — enter up to 10 topics, one per line. All decks generate automatically in sequence.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Left: input ── */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Topics — one per line (max 10)</label>
            <textarea
              rows={8}
              value={bulkTopics}
              onChange={e => setBulkTopics(e.target.value)}
              disabled={isRunning}
              placeholder={"Q3 Investor Update — SaaS Growth\nProduct Launch: Enterprise AI Suite\nCompany All-Hands — 2026 Strategy\nSales Kickoff — Q4 Pipeline Review\nNew Employee Onboarding Overview"}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40 resize-none font-mono disabled:opacity-50"
            />
          </div>

          {/* Parsed pills */}
          {topics.length > 0 && !isRunning && decks.length === 0 && (
            <div>
              <p className="text-xs font-bold text-slate-500 mb-2">{topics.length} topic{topics.length > 1 ? 's' : ''} detected</p>
              <div className="flex flex-wrap gap-1.5">
                {topics.map((t, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-white/8 border border-white/12 rounded-lg">
                    <span className="w-4 h-4 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="text-xs text-slate-300 truncate max-w-[140px]">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tone selector */}
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Tone for All Decks</label>
            <div className="flex flex-wrap gap-1.5">
              {TONES.map(t => (
                <button key={t} onClick={() => setTone(t)} disabled={isRunning} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 ${tone === t ? 'bg-amber-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/15 hover:text-white'}`}>{t}</button>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          {!isRunning && !allDone && (
            <button onClick={startBatch} disabled={topics.length === 0} className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-40 text-white font-bold rounded-xl cursor-pointer hover:opacity-90 text-sm transition-all whitespace-nowrap">
              <i className="ri-stack-line text-sm"></i>Generate {topics.length > 0 ? topics.length : ''} Deck{topics.length !== 1 ? 's' : ''}
            </button>
          )}

          {allDone && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/15 border border-emerald-500/30 rounded-xl">
                <i className="ri-check-double-line text-emerald-400"></i>
                <span className="text-xs font-bold text-emerald-300">All {doneCount} decks generated successfully!</span>
              </div>
              <button onClick={onSignUp} className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap">
                <i className="ri-download-2-line"></i>Download All Decks — Sign In
              </button>
              <button onClick={handleReset} className="w-full flex items-center justify-center gap-2 py-2 bg-white/8 border border-white/15 text-slate-400 text-xs font-semibold rounded-xl cursor-pointer hover:bg-white/12 hover:text-white transition-all whitespace-nowrap">
                <i className="ri-restart-line"></i>Start Over
              </button>
            </div>
          )}
        </div>

        {/* ── Right: queue ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Batch Queue</p>
            {decks.length > 0 && <span className="text-xs text-slate-500">{doneCount}/{decks.length} done</span>}
          </div>

          {decks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-52 rounded-2xl border-2 border-dashed border-white/10 bg-white/3 gap-3">
              <i className="ri-stack-line text-3xl text-slate-600"></i>
              <p className="text-xs text-slate-600 text-center leading-relaxed">Enter topics on the left<br />and click Generate to start</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              {decks.map((deck, i) => (
                <div key={deck.id} className={`rounded-xl border overflow-hidden transition-all ${deck.status === 'done' ? 'border-emerald-500/30 bg-emerald-500/8' : deck.status === 'generating' ? 'border-amber-500/40 bg-amber-500/8' : 'border-white/10 bg-white/5'}`}>
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${deck.status === 'done' ? 'bg-emerald-500' : deck.status === 'generating' ? 'bg-amber-500' : 'bg-white/10'}`}>
                      {deck.status === 'done'
                        ? <i className="ri-check-line text-white text-xs"></i>
                        : deck.status === 'generating'
                        ? <i className="ri-loader-4-line text-white text-xs animate-spin"></i>
                        : <span className="text-xs font-bold text-slate-500">{i + 1}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${deck.status === 'done' ? 'text-emerald-300' : deck.status === 'generating' ? 'text-amber-300' : 'text-slate-500'}`}>{deck.topic}</p>
                      {deck.status === 'done'      && <p className="text-xs text-slate-500">{deck.slideCount} slides · {tone}</p>}
                      {deck.status === 'queued'    && <p className="text-xs text-slate-600">Queued…</p>}
                      {deck.status === 'generating' && <p className="text-xs text-amber-500/80">Building slides…</p>}
                    </div>
                    {deck.status === 'done' && (
                      <button onClick={onSignUp} className="flex-shrink-0 px-2.5 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-bold rounded-lg cursor-pointer hover:bg-emerald-500/30 transition-all whitespace-nowrap">Open</button>
                    )}
                    {deck.status === 'generating' && <span className="text-xs font-bold text-amber-400 flex-shrink-0">{Math.round(deck.progress)}%</span>}
                  </div>
                  {deck.status === 'generating' && (
                    <div className="h-0.5 bg-white/5">
                      <div className="h-full bg-gradient-to-r from-amber-500 to-orange-400 transition-all duration-200" style={{ width: `${deck.progress}%` }} />
                    </div>
                  )}
                  {deck.status === 'done' && <div className="h-0.5 bg-emerald-500/40" />}
                </div>
              ))}
            </div>
          )}

          {/* Overall progress bar */}
          {isRunning && decks.length > 0 && (
            <div className="mt-3 px-3 py-2 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400">Overall Progress</span>
                <span className="text-xs font-bold text-amber-400">{doneCount}/{decks.length} complete</span>
              </div>
              <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-orange-400 rounded-full transition-all duration-300" style={{ width: `${(doneCount / decks.length) * 100}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
